/**
 * List Page Header Component
 * Reusable header for list pages with title, export, and add buttons
 */

import React from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { AddButton, ExportButton } from "./ActionButton";
import { HelpButton } from "./HelpButton";

export interface ListPageHeaderProps {
  /** Page title */
  title: string;
  /** Add button label */
  addLabel?: string;
  /** Called when add button clicked */
  onAdd?: () => void;
  /** Called when export button clicked */
  onExport?: () => void;
  /** Whether export is in progress */
  isExporting?: boolean;
  /** Whether to show loading skeleton */
  isLoading?: boolean;
  /** Additional action buttons */
  actions?: React.ReactNode;
  /** Help guide slug for contextual help button */
  helpSlug?: string;
}

export const ListPageHeader: React.FC<ListPageHeaderProps> = ({
  title,
  addLabel = "Add New",
  onAdd,
  onExport,
  isExporting = false,
  isLoading = false,
  actions,
  helpSlug,
}) => {
  if (isLoading) {
    return (
      <Box
        role="status"
        aria-busy="true"
        aria-label="Loading header"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 2, sm: 0 },
        mb: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography variant="h4" component="h1" sx={{
          fontWeight: 700
        }}>
          {title}
        </Typography>
        {helpSlug && <HelpButton slug={helpSlug} />}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {actions}
        {onExport && (
          <ExportButton
            label="Export CSV"
            onClick={onExport}
            isLoading={isExporting}
          />
        )}
        {onAdd && <AddButton label={addLabel} onClick={onAdd} />}
      </Box>
    </Box>
  );
};

export default ListPageHeader;
